/**
 * IsIzinleriModal.jsx — Staff tarafı İş İzinleri onay/red ekranı (backlog #69(4))
 *
 * TumStandlarListPage.jsx'teki İş İzni karesi eskiden genel StandDurumuModal'ı açıyordu;
 * artık kendi modalını açıyor. Firma tarafındaki IsIzinCard/IsIzinTalepleriPanel
 * (FirmaTalepStandDetailPage.jsx) BİLEREK yeniden kullanılmadı/değiştirilmedi — onlar salt
 * okunur, firma-yüzlü bileşenler (21 binin üzerinde firma girişi o akışı kullanıyor) ve
 * onay/red kontrolü eklemek o dosyayı staff ihtiyacına göre riske atardı. Bu dosya yalnızca
 * VERİ KATMANINI paylaşıyor: standTalepService.js'teki getIsIzinleri/IS_IZNI_KATEGORILERI/
 * TALEP_STATUS + backlog #69'da eklenen reviewTalepRequest.
 *
 * PATCH /stands/talepler/{standId}/talepler/{requestId}/review — status: 0 Beklemede,
 * 1 Onayla, 2 Reddet (rejectReason zorunlu). Her üç yön de destekleniyor (backend yorumu:
 * "the only way back from Onaylı ... is setting status back to 0 here").
 */
import React, { useEffect, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, Row, Col, Card, CardBody, Badge, Button, Input,
  Spinner, Alert,
} from "reactstrap";
import {
  Cog, PartyPopper, CookingPot, Waves, Flame, PawPrint, Scissors, Crosshair,
  Check, X, RotateCcw,
} from "lucide-react";
import {
  getIsIzinleri, reviewTalepRequest, IS_IZNI_KATEGORILERI, TALEP_STATUS,
} from "../../../../services/standTalepService";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../../../i18n";

const IS_IZNI_ICONS = {
  makine: Cog, gosteri: PartyPopper, pisirme: CookingPot, sivi: Waves,
  sicak: Flame, hayvan: PawPrint, kesici: Scissors, silah: Crosshair,
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), { day: "2-digit", month: "short", year: "numeric" });
}

function IsIzniReviewCard({ category, request, onReview, busy }) {
  const { t } = useTranslation("moduller");
  const Icon = IS_IZNI_ICONS[category.key] ?? Cog;
  const statusInfo = TALEP_STATUS[request.status] ?? TALEP_STATUS[0];
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex align-items-center gap-2 mb-2">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{ width: 44, height: 44, background: statusInfo.hex }}
          >
            <Icon size={20} color="#fff" />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="text-truncate" title={request.yapilacakIs || category.label}>
              {request.yapilacakIs || category.label}
            </div>
            <div className="small text-muted">{fmtDate(request.startDate)} – {fmtDate(request.endDate)}</div>
          </div>
          <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
        </div>

        {request.rejectReason && (
          <div className="small text-danger mb-2">{t("isIzinleriModal.redGerekcesi")}{" "}{request.rejectReason}</div>
        )}

        {rejecting ? (
          <div className="d-flex gap-1">
            <Input
              bsSize="sm" placeholder={t("isIzinleriModal.redGerekcesiZorunlu")} value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button
              size="sm" color="danger" disabled={busy || !reason.trim()}
              onClick={() => onReview(request.requestId, 2, reason.trim())}
            >
              <Check size={14} />
            </Button>
            <Button size="sm" color="light" className="border" disabled={busy} onClick={() => setRejecting(false)}>
              <X size={14} />
            </Button>
          </div>
        ) : (
          <div className="d-flex gap-1">
            <Button
              size="sm" color="success" outline={request.status === 1} disabled={busy || request.status === 1}
              onClick={() => onReview(request.requestId, 1)}
            >
              <Check size={14} className="me-1" />{t("isIzinleriModal.onayla")}
            </Button>
            <Button
              size="sm" color="danger" outline disabled={busy || request.status === 2}
              onClick={() => setRejecting(true)}
            >
              <X size={14} className="me-1" />{t("isIzinleriModal.reddet")}
            </Button>
            {request.status !== 0 && (
              <Button size="sm" color="light" className="border" disabled={busy} title={t("isIzinleriModal.beklemedeYeAl")}
                onClick={() => onReview(request.requestId, 0)}>
                <RotateCcw size={14} />
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default function IsIzinleriModal({ stand, onClose }) {
  const { t } = useTranslation("moduller");
  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getIsIzinleri(stand.standId, { forceLive: true });
        if (!ignore) setRequests(data || []);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [stand.standId, reloadToken]);

  async function handleReview(requestId, status, rejectReason) {
    setBusyId(requestId);
    setError(null);
    try {
      await reviewTalepRequest(stand.standId, requestId, status, rejectReason);
      setReloadToken((n) => n + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const byCategory = {};
  (requests || []).forEach((r) => { byCategory[r.categoryKey] = r; });
  const cards = IS_IZNI_KATEGORILERI.filter((c) => byCategory[c.key]?.hasRequest);

  return (
    <Modal isOpen toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        {t("isIzinleriModal.isIzinleri", { standName: stand.standName })}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" className="mb-3">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : cards.length === 0 ? (
          <Alert color="secondary" className="mb-0">{t("isIzinleriModal.buStandIcinHenuzIs")}</Alert>
        ) : (
          <Row className="g-3">
            {cards.map((c) => (
              <Col md={6} key={c.key}>
                <IsIzniReviewCard
                  category={c} request={byCategory[c.key]} onReview={handleReview}
                  busy={busyId === byCategory[c.key]?.requestId}
                />
              </Col>
            ))}
          </Row>
        )}
      </ModalBody>
    </Modal>
  );
}
